import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, inr, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Package, CheckCircle2, Truck, MapPin, Search } from "lucide-react";

const STEPS = ["PLACED", "CONFIRMED", "LABEL_GENERATED", "PICKED_UP", "IN_TRANSIT", "OUT_FOR_DELIVERY", "DELIVERED"];
const LABELS = { PLACED: "Order Placed", CONFIRMED: "Confirmed", LABEL_GENERATED: "Label Generated", PICKED_UP: "Picked Up", IN_TRANSIT: "In Transit", OUT_FOR_DELIVERY: "Out for Delivery", DELIVERED: "Delivered" };

export default function TrackOrder() {
  const { orderNumber } = useParams();
  const [input, setInput] = useState(orderNumber || "");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const track = async (num) => {
    const id = (num || input).trim();
    if (!id) { toast.error("Enter your Order ID"); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/track/${id}`);
      setOrder(data);
    } catch (e) { setOrder(null); toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (orderNumber) track(orderNumber); /* eslint-disable-next-line */ }, [orderNumber]);

  const currentIdx = order ? STEPS.indexOf(order.status) : -1;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-12">
      <div className="text-center mb-8">
        <p className="accent-label">Shiprocket Live Tracking</p>
        <h1 className="font-serif text-3xl lg:text-4xl font-bold text-rose-950 mt-2">Track Your Order</h1>
      </div>
      <div className="flex gap-2 max-w-md mx-auto mb-10">
        <Input value={input} onChange={(e) => setInput(e.target.value.toUpperCase())} placeholder="Enter Order ID e.g. RW2610001" className="h-12" data-testid="track-input" />
        <Button onClick={() => track()} disabled={loading} className="bg-rose-950 hover:bg-rose-900 h-12 px-6" data-testid="track-submit-btn"><Search className="w-4 h-4 mr-1" />Track</Button>
      </div>

      {order && (
        <div className="fade-up">
          <div className="bg-white border border-rose-900/15 rounded-2xl p-6 mb-6">
            <div className="flex flex-wrap justify-between gap-3 mb-4">
              <div><p className="accent-label">Order ID</p><p className="font-serif text-xl font-bold text-rose-950">{order.order_number}</p></div>
              <div className="text-right"><p className="accent-label">Status</p><p className="font-semibold text-rose-950">{LABELS[order.status] || order.status}</p></div>
            </div>
            {order.tracking?.tracking_id && (
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 text-sm flex items-center gap-2 mb-2" data-testid="track-courier-info">
                <Truck className="w-4 h-4 text-violet-700" />Courier: <b>{order.tracking.courier || "Shiprocket"}</b> · Tracking ID: <b>{order.tracking.tracking_id}</b>
              </div>
            )}
            {order.live && (
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 text-sm mb-2" data-testid="track-live-shiprocket">
                <p className="font-medium text-violet-800 flex items-center gap-1.5"><Truck className="w-4 h-4" />Live Shiprocket Status: <b>{order.live.current_status}</b>{order.live.courier ? ` · ${order.live.courier}` : ""}</p>
                {order.live.activities?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {order.live.activities.map((a, i) => (
                      <p key={i} className="text-xs text-violet-700">{(a.date || "").slice(0, 16)} — {a.status}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
            {/* Progress */}
            {currentIdx >= 0 && (
              <div className="mt-6 space-y-0">
                {STEPS.map((s, i) => {
                  const done = i <= currentIdx;
                  return (
                    <div key={s} className="flex gap-4" data-testid={`track-step-${s}`}>
                      <div className="flex flex-col items-center">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center ${done ? "bg-emerald-600 text-white" : "bg-rose-900/10 text-rose-900/40"}`}>
                          {done ? <CheckCircle2 className="w-4 h-4" /> : <div className="w-2 h-2 rounded-full bg-current" />}
                        </div>
                        {i < STEPS.length - 1 && <div className={`w-0.5 h-8 ${i < currentIdx ? "bg-emerald-600" : "bg-rose-900/10"}`} />}
                      </div>
                      <div className={`pb-6 ${done ? "text-rose-950" : "text-muted-foreground"}`}>
                        <p className="font-medium text-sm">{LABELS[s]}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="bg-white border border-rose-900/15 rounded-2xl p-6">
            <h3 className="font-serif text-lg font-semibold text-rose-950 mb-3">Items</h3>
            {order.items?.map((it, i) => (
              <div key={i} className="flex justify-between text-sm py-1.5">
                <span>{it.name} ({it.sku}) · {it.size} × {it.qty}</span>
                <span className="font-medium">{inr(it.price * it.qty)}</span>
              </div>
            ))}
            <div className="flex justify-between font-serif text-lg font-bold text-rose-950 border-t border-rose-900/10 mt-3 pt-3"><span>Total</span><span>{inr(order.total)}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}
